import { useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar,
  Clock,
  User,
  MoreVertical,
  Play,
  CheckCircle2,
  XCircle,
  Briefcase,
  Wrench,
  Eye,
  Pencil,
  RotateCcw,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

// Labels para subtipos
const SUBTYPE_LABELS: Record<string, string> = {
  training: "Capacitación",
  report: "Informe",
  audit: "Auditoría",
  meeting: "Reunión",
  documentation: "Documentación",
  cleaning: "Limpieza",
  maintenance: "Mantenimiento",
  organization: "Organización",
  inventory: "Inventario",
  equipment_check: "Revisión de Equipos",
};

interface TaskCardProps {
  task: any;
  getStatusBadge: (status: string) => JSX.Element;
  getPriorityBadge: (priority: string) => JSX.Element;
  onUpdate: () => void;
  onViewDetails?: (task: any) => void;
  onEdit?: (task: any) => void;
}

export function TaskCard({
  task,
  getStatusBadge,
  getPriorityBadge,
  onUpdate,
  onViewDetails,
  onEdit,
}: TaskCardProps) {
  const [loading, setLoading] = useState(false);

  const updateStatus = async (newStatus: string) => {
    setLoading(true);
    try {
      const updateData: any = { status: newStatus };

      if (newStatus === "in_progress" && !task.started_at) {
        updateData.started_at = new Date().toISOString();
      }

      if (newStatus === "completed") {
        updateData.completed_at = new Date().toISOString();
        if (task.started_at) {
          const startTime = new Date(task.started_at).getTime();
          const endTime = Date.now();
          updateData.actual_duration_minutes = Math.round(
            (endTime - startTime) / 60000
          );
        }
      }

      const { error } = await api
        .from("internal_tasks")
        .update(updateData)
        .eq("id", task.id);

      if (error) throw error;

      toast.success(
        newStatus === "completed"
          ? "Tarea completada"
          : newStatus === "in_progress"
          ? "Tarea iniciada"
          : "Estado actualizado"
      );
      onUpdate();
    } catch (error: any) {
      toast.error(error.message || "Error al actualizar");
    } finally {
      setLoading(false);
    }
  };

  const TypeIcon = task.task_type === "administrative" ? Briefcase : Wrench;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <TypeIcon className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-mono">
              {task.task_number}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Opciones siempre visibles */}
              <DropdownMenuItem onClick={() => onViewDetails?.(task)}>
                <Eye className="w-4 h-4 mr-2" />
                Ver detalles
              </DropdownMenuItem>
              {task.status !== "completed" && task.status !== "cancelled" && (
                <DropdownMenuItem onClick={() => onEdit?.(task)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {/* Opciones de estado */}
              {task.status === "pending" && (
                <DropdownMenuItem
                  onClick={() => updateStatus("in_progress")}
                  disabled={loading}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar
                </DropdownMenuItem>
              )}
              {task.status === "in_progress" && (
                <DropdownMenuItem
                  onClick={() => updateStatus("completed")}
                  disabled={loading}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Completar
                </DropdownMenuItem>
              )}
              {(task.status === "completed" || task.status === "cancelled") && (
                <DropdownMenuItem
                  onClick={() => updateStatus("pending")}
                  disabled={loading}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reabrir
                </DropdownMenuItem>
              )}
              {task.status !== "cancelled" && task.status !== "completed" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => updateStatus("cancelled")}
                    disabled={loading}
                    className="text-red-600"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <h4 className="font-semibold line-clamp-2">{task.title}</h4>
          {task.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {task.description}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {getStatusBadge(task.status)}
          {getPriorityBadge(task.priority)}
          <Badge variant="outline" className="text-xs">
            {SUBTYPE_LABELS[task.task_subtype] || task.task_subtype}
          </Badge>
        </div>

        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          {task.assigned_profile && (
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {task.assigned_profile.full_name}
            </div>
          )}
          {task.due_date && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {format(new Date(task.due_date), "dd MMM yyyy", { locale: es })}
            </div>
          )}
          {task.estimated_duration_minutes && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {task.estimated_duration_minutes} min estimados
            </div>
          )}
        </div>

        {/* Quick actions */}
        {task.status === "pending" && (
          <Button
            size="sm"
            className="w-full"
            variant="outline"
            onClick={() => updateStatus("in_progress")}
            disabled={loading}
          >
            <Play className="w-4 h-4 mr-2" />
            Iniciar Tarea
          </Button>
        )}
        {task.status === "in_progress" && (
          <Button
            size="sm"
            className="w-full bg-green-600 hover:bg-green-700"
            onClick={() => updateStatus("completed")}
            disabled={loading}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Completar
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
